import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '@components/ui';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useInvoiceWizard } from '../hooks/useInvoiceWizard';
import { invoiceService } from '../services/invoiceService';
import { Step1CustomerSelection } from './wizard/Step1CustomerSelection';
import { StepReference } from './wizard/StepReference';
import { Step2InvoiceDetails } from './wizard/Step2InvoiceDetails';
import { Step3AddItems } from './wizard/Step3AddItems';
import { Step4AdditionalInfo } from './wizard/Step4AdditionalInfo';
import { Step5Review } from './wizard/Step5Review';
import { ErrorModal } from './wizard/ErrorModal';
import { SuccessModal } from './wizard/SuccessModal';
import { ProfileIncompleteModal } from './ProfileIncompleteModal';
import { mapInvoiceToAPIFormat, validateInvoiceData, mapAPIFormatToWizard } from '../services/invoiceMapper';
import { companyService } from '@features/companies/services/companyService';
import { useBranchOffices } from '@features/companies/hooks/useBranchOffices';
import { useState, useEffect, useCallback } from 'react';
import { showError, showErrorList } from '@/utils/sweetAlert';
// SUBSCRIPTIONS_DISABLED: import { useConsumption } from '@features/subscriptions/hooks';
import Swal from 'sweetalert2';
import { trackingHelpers } from '@/services/mixpanel';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';
import { useReportError, useSupportConfig } from '@features/support';

// Base steps for normal invoice
const BASE_STEPS = [
  { id: 1, name: 'Cliente', description: 'Seleccionar cliente' },
  { id: 2, name: 'Detalles', description: 'Información de factura' },
  { id: 3, name: 'Items', description: 'Agregar productos' },
  { id: 4, name: 'Notas', description: 'Información adicional' },
  { id: 5, name: 'Revisar', description: 'Confirmar y emitir' },
];

// Reference step for credit/debit notes
const REFERENCE_STEP = { id: 2, name: 'Referencia', description: 'Documento referenciado' };

interface ErrorDetail {
  code: string;
  message: string;
}

interface ErrorState {
  isOpen: boolean;
  title: string;
  message: string;
  details?: ErrorDetail[];
}

interface SuccessState {
  isOpen: boolean;
  title: string;
  message: string;
  status: 'success' | 'pending';
  responseCode?: string;
  additionalInfo?: string;
  electronicId?: string;
}

export function InvoiceWizardPage() {
  const navigate = useNavigate();
  /*
   * Asistente de soporte para el rechazo de la DGI. Cuando esta activo, su modal
   * REEMPLAZA al ErrorModal en vez de sumarse: muestra la misma lista de codigos
   * y ademas la explicacion. Apilar los dos obligaba a cerrar uno para leer el
   * otro. Si el asistente esta apagado, sigue el ErrorModal de siempre.
   */
  const reportError = useReportError();
  const { assistantEnabled } = useSupportConfig();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [isCheckingCertificates, setIsCheckingCertificates] = useState(true);
  const [isLoadingReference, setIsLoadingReference] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [error, setError] = useState<ErrorState>({
    isOpen: false,
    title: '',
    message: '',
    details: undefined,
  });

  const [success, setSuccess] = useState<SuccessState>({
    isOpen: false,
    title: '',
    message: '',
    status: 'success',
    responseCode: undefined,
    additionalInfo: undefined,
    electronicId: undefined,
  });

  const {
    currentStep,
    wizardData,
    updateWizardData,
    addItem,
    updateItem,
    removeItem,
    nextStep,
    previousStep,
    goToStep,
    resetWizard,
  } = useInvoiceWizard();

  // Hook para obtener sucursales (necesario para buscar ID por código)
  const { data: branchOfficesData } = useBranchOffices();

  /* SUBSCRIPTIONS_DISABLED
  const {
    validateCanAddInvoice,
    getRemainingInvoices,
    currentPlan,
    consumption,
    isLoading: isLoadingPlan
  } = useConsumption();
  */

  // Cross-tab lock: detect if another tab started submitting
  const LOCK_KEY = 'invoice_submit_lock';
  const LOCK_MAX_AGE_MS = 2 * 60 * 1000; // 2 minutes

  const acquireCrossTabLock = useCallback((): boolean => {
    try {
      const existing = localStorage.getItem(LOCK_KEY);
      if (existing) {
        const lockTime = parseInt(existing, 10);
        if (Date.now() - lockTime < LOCK_MAX_AGE_MS) {
          return false; // Another tab is submitting
        }
      }
      localStorage.setItem(LOCK_KEY, String(Date.now()));
      return true;
    } catch {
      return true; // localStorage unavailable, proceed
    }
  }, []);

  const releaseCrossTabLock = useCallback(() => {
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Listen for cross-tab lock changes
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOCK_KEY && e.newValue && !isSubmitting) {
        // Another tab acquired the lock
        console.warn('[InvoiceWizard] Another tab is submitting an invoice');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [isSubmitting]);

  // Debug: ver estructura completa
  console.log('🔍 branchOfficesData raw:', branchOfficesData);
  console.log('🔍 branchOfficesData type:', typeof branchOfficesData);
  console.log('🔍 branchOfficesData keys:', branchOfficesData ? Object.keys(branchOfficesData) : 'null');

  const branchOffices = (branchOfficesData as any)?.branchOffices || [];
  console.log('🔍 branchOffices extracted:', branchOffices);
  console.log('🔍 branchOffices is array?:', Array.isArray(branchOffices));

  console.log('🔍 InvoiceWizardPage render:', {
    currentStep,
    wizardDataKeys: Object.keys(wizardData),
    itemsCount: wizardData.items?.length,
  });

  // Verifica los certificados de la compañía al cargar la página (con reintentos
  // ante errores transitorios). Si el perfil está incompleto, muestra el modal.
  useEffect(() => {
    const checkCertificates = async (retries = 2): Promise<void> => {
      try {
        const result = await companyService.checkClientCertificates();

        if (!result.hasCertificates) {
          setShowProfileModal(true);
        }
      } catch (error: any) {
        console.error('Error checking certificates:', error);

        // Si el error es 400 (no hay compañía seleccionada), volver al home
        if (error?.response?.status === 400) {
          navigate(homeRoute);
          return;
        }

        // En errores transitorios (500, 401, timeout), reintentar antes de rendirse
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          return checkCertificates(retries - 1);
        }

        // Agotados los reintentos, permitir continuar en lugar de bloquear con el modal
        console.warn('Could not verify certificates after retries, allowing user to proceed');
      } finally {
        setIsCheckingCertificates(false);
      }
    };

    checkCertificates();
  }, [navigate, homeRoute]);

  // Load referenced invoice for Credit/Debit Notes OR Correction Mode
  useEffect(() => {
    const type = searchParams.get('type'); // 'credit' o 'debit'
    const mode = searchParams.get('mode'); // 'correction'
    const sameCufe = searchParams.get('sameCufe'); // 'true' o 'false'
    const ref = searchParams.get('ref'); // ID de la factura referenciada

    // Si no hay ref, no hay nada que cargar
    if (!ref) {
      return;
    }

    // Si no hay type ni mode, es una factura normal
    if (!type && !mode) {
      return;
    }

    // Si es modo clon, no necesitamos type
    // (mode=clone solo requiere ref)

    const loadReferencedInvoice = async () => {
      console.log('📋 Loading referenced invoice:', { type, mode, sameCufe, ref });
      setIsLoadingReference(true);

      try {
        // Obtener la factura referenciada
        const referencedInvoice = await invoiceService.getInvoiceById(ref);

        console.log('✅ Referenced invoice loaded:', referencedInvoice);
        console.log('📦 Payload type:', typeof referencedInvoice.payload);
        console.log('📦 Payload preview:', JSON.stringify(referencedInvoice.payload)?.substring(0, 200));

        // Usar la función reverse mapper para convertir el payload de la API a formato Wizard
        console.log('🔄 Calling mapAPIFormatToWizard...');
        const wizardDataFromInvoice = mapAPIFormatToWizard(referencedInvoice.payload, {
          billNumber: referencedInvoice.billNumber,
          cufe: referencedInvoice.cufe,
          createdDate: referencedInvoice.createdDate,
          id: ref,
        });
        console.log('✅ mapAPIFormatToWizard completed:', Object.keys(wizardDataFromInvoice).length, 'keys');

        // Modo Corrección
        if (mode === 'correction') {
          const isFixingEBill = sameCufe === 'true';
          console.log('🔧 Correction mode:', isFixingEBill ? 'Same CUFE' : 'New CUFE');

          // Extraer datos originales del payload para corrección con mismo CUFE
          const originalPayload = typeof referencedInvoice.payload === 'string'
            ? JSON.parse(referencedInvoice.payload)
            : referencedInvoice.payload;
          const dGen = originalPayload?.dGen || {};
          const gEmis = dGen?.gEmis || {};

          // Pre-llenar el wizard con los datos de la factura a corregir
          updateWizardData({
            ...wizardDataFromInvoice,
            // Mantener el mismo documentMode (invoice)
            documentMode: 'invoice',
            // Marcar que es una corrección
            isFixingEBill,
            referencedInvoiceId: ref,
            onFixingEBillId: ref,
            // Datos originales necesarios para corrección con mismo CUFE
            originalCufe: referencedInvoice.cufe || dGen.dId,
            originalSeg: dGen.dSeg,
            originalInvoiceNumber: dGen.dNroDF || referencedInvoice.billNumber,
            originalBillingPointCode: dGen.dPtoFacDF,
            originalBranchOfficeCode: gEmis.dSucEm,
            originalEmissionDate: dGen.dFechaEm,
          });

          console.log('✅ Wizard configured for correction mode, isFixingEBill:', isFixingEBill);
          if (isFixingEBill) {
            console.log('📋 Original data saved:', {
              cufe: referencedInvoice.cufe || dGen.dId,
              seg: dGen.dSeg,
              invoiceNumber: dGen.dNroDF,
              billingPointCode: dGen.dPtoFacDF,
              branchOfficeCode: gEmis.dSucEm,
              emissionDate: dGen.dFechaEm,
            });
          }
        }
        // Modo Clonar Factura
        else if (mode === 'clone') {
          console.log('📋 Clone mode: pre-filling wizard with invoice data');

          updateWizardData({
            ...wizardDataFromInvoice,
            // Resetear campos para factura nueva
            invoiceNumber: '',
            invoiceNumberMode: 'auto',
            invoiceDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
            documentMode: 'invoice',
            referencedInvoiceId: undefined,
            gDFRef: undefined,
            isFixingEBill: false,
            onFixingEBillId: undefined,
            originalCufe: undefined,
            originalSeg: undefined,
            originalInvoiceNumber: undefined,
            originalBillingPointCode: undefined,
            originalBranchOfficeCode: undefined,
            originalEmissionDate: undefined,
          });

          console.log('✅ Wizard configured for clone mode');
        }
        // Modo Credit/Debit Note
        else if (type) {
          // Determinar el documentMode y documentType
          const documentMode = type === 'credit' ? 'credit-note' : 'debit-note';
          const documentType = type === 'credit' ? '04' : '05';

          // Pre-llenar el wizard con los datos de la factura referenciada
          // NOTA: El branchOfficeId se buscará en un useEffect separado cuando las sucursales estén disponibles
          updateWizardData({
            ...wizardDataFromInvoice,
            // Sobrescribir con los valores específicos para la nota
            documentMode,
            documentType,
            referencedInvoiceId: ref,
            gDFRef: {
              dNroFacIE: referencedInvoice.billNumber || null,
              dNroFacPap: null,
              dCufeRef: referencedInvoice.cufe || '',
              dFechaDFRef: referencedInvoice.createdDate || new Date().toISOString(),
            },
          });

          console.log('✅ Wizard configured for', documentMode, 'with pre-filled data');
        }
      } catch (error) {
        console.error('❌ Error loading referenced invoice:', error);

        // Mostrar el error detallado
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        const errorStack = error instanceof Error ? error.stack : '';

        console.error('Error details:', { errorMessage, errorStack });

        showError(
          'Error al cargar factura',
          `No se pudo cargar la factura referenciada: ${errorMessage}`
        );
        // Redirigir a la lista de facturas
        setTimeout(() => navigate('/invoices'), 3000);
      } finally {
        setIsLoadingReference(false);
      }
    };

    loadReferencedInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Solo ejecutar cuando cambian los parámetros de búsqueda

  // Buscar el ID de la sucursal cuando estén disponibles
  useEffect(() => {
    // Solo ejecutar si tenemos código de sucursal pero no ID
    if (wizardData.branchOfficeAddressCode && !wizardData.branchOfficeId && Array.isArray(branchOffices) && branchOffices.length > 0) {
      console.log('🔍 Searching for branch office with code:', `"${wizardData.branchOfficeAddressCode}"`);
      console.log('📋 Available branch offices:', branchOffices.length);
      console.log('📋 Available codes:', branchOffices.map((b: any) => `"${b.code}"`).join(', '));

      // Buscar por código exacto
      const foundBranch = branchOffices.find(
        (branch: any) => String(branch.code) === String(wizardData.branchOfficeAddressCode)
      );

      if (foundBranch) {
        console.log('✅ Branch office found:', {
          id: foundBranch.id,
          code: foundBranch.code,
          name: foundBranch.name,
          address: foundBranch.address,
          province: foundBranch.province,
          district: foundBranch.district,
          township: foundBranch.township,
          fiscalCode: foundBranch.fiscalCode,
        });

        // Actualizar con todos los datos de la sucursal
        updateWizardData({
          branchOfficeId: foundBranch.id,
          branchOfficeName: foundBranch.name,
          branchOfficeAddress: foundBranch.address || foundBranch.fullAddress || '',
          branchOfficeProvince: foundBranch.province || '',
          branchOfficeDistrict: foundBranch.district || '',
          branchOfficeTownship: foundBranch.township || '',
          branchOfficeFiscalCode: foundBranch.fiscalCode || '',
          branchOfficeLatitude: foundBranch.coordinates?.latitude?.toString() || '',
          branchOfficeLongitude: foundBranch.coordinates?.longitude?.toString() || '',
        });
      } else {
        console.warn('⚠️ Branch office not found for code:', `"${wizardData.branchOfficeAddressCode}"`);
        console.warn('Available codes:', branchOffices.map((b: any) => `code: "${b.code}", name: "${b.name}"`).join(' | '));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardData.branchOfficeAddressCode, wizardData.branchOfficeId, branchOffices.length]); // No incluir updateWizardData para evitar loop

  const handleCompleteInfo = () => {
    setShowProfileModal(false);
    // Redirigir a la pestaña de información fiscal / certificados
    navigate('/company/settings?tab=fiscal');
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Guard contra múltiples clicks

    // Cross-tab lock: prevent simultaneous submissions from different tabs
    if (!acquireCrossTabLock()) {
      showError(
        'Envío en progreso',
        'Ya hay un envío de factura en progreso en otra pestaña. Por favor espera a que termine.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      /* SUBSCRIPTIONS_DISABLED
      if (isLoadingPlan) {
        await Swal.fire({ title: 'Cargando...', text: 'Verificando límites del plan', icon: 'info', confirmButtonText: 'Entendido' });
        setIsSubmitting(false);
        return;
      }
      const planValidation = validateCanAddInvoice(true);
      if (!planValidation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de facturas alcanzado',
          text: planValidation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });
        if (result.isConfirmed) { window.location.href = '/subscriptions/plans'; }
        setIsSubmitting(false);
        return;
      }
      */

      // VALIDAR DATOS ANTES DE MAPEAR
      console.log('='.repeat(80));
      console.log('🔍 VALIDANDO DATOS DE LA FACTURA');
      console.log('='.repeat(80));

      const validation = validateInvoiceData(wizardData);
      if (!validation.valid) {
        console.error('❌ Errores de validación:', validation.errors);
        showErrorList('Errores de Validación', validation.errors);
        setIsSubmitting(false);
        return;
      }

      // Mostrar advertencias DGI (notificaciones, no bloquean)
      if (validation.warnings && validation.warnings.length > 0) {
        console.warn('⚠️ Advertencias DGI:', validation.warnings);
      }

      console.log('✅ Validación exitosa\n');

      // LOG COMPLETO DE TODA LA DATA
      console.log('='.repeat(80));
      console.log('📋 DATOS COMPLETOS DE LA FACTURA - OBJETO EN BRUTO (WIZARD)');
      console.log('='.repeat(80));

      console.log('\n🏢 DATOS DEL EMISOR:');
      console.log(JSON.stringify({
        issuerName: wizardData.issuerName,
        issuerCode: wizardData.issuerCode,
        issuerDv: wizardData.issuerDv,
        issuerEmail: wizardData.issuerEmail,
        issuerPhone: wizardData.issuerPhone,
        issuerImage: wizardData.issuerImage,
      }, null, 2));

      console.log('\n🏪 DATOS DE LA SUCURSAL:');
      console.log(JSON.stringify({
        branchOfficeId: wizardData.branchOfficeId,
        branchOfficeName: wizardData.branchOfficeName,
        branchOfficeAddress: wizardData.branchOfficeAddress,
        branchOfficeAddressCode: wizardData.branchOfficeAddressCode,
        branchOfficeLatitude: wizardData.branchOfficeLatitude,
        branchOfficeLongitude: wizardData.branchOfficeLongitude,
        branchOfficeProvince: wizardData.branchOfficeProvince,
        branchOfficeDistrict: wizardData.branchOfficeDistrict,
        branchOfficeTownship: wizardData.branchOfficeTownship,
        branchOfficeFiscalCode: wizardData.branchOfficeFiscalCode,
        billingPointCode: wizardData.billingPointCode,
      }, null, 2));

      console.log('\n👤 DATOS DEL RECEPTOR/CLIENTE:');
      console.log(JSON.stringify({
        receiverType: wizardData.receiverType,
        customerId: wizardData.customerId,
        customerName: wizardData.customerName,
        customerRuc: wizardData.customerRuc,
        customerDv: wizardData.customerDv,
        customerRucType: wizardData.customerRucType,
        customerEmail: wizardData.customerEmail,
        customerPhone: wizardData.customerPhone,
      }, null, 2));

      console.log('\n📍 DIRECCIÓN DEL CLIENTE:');
      console.log(JSON.stringify({
        customerAddress: wizardData.customerAddress,
        customerCountry: wizardData.customerCountry,
        customerProvince: wizardData.customerProvince,
        customerDistrict: wizardData.customerDistrict,
        customerTownship: wizardData.customerTownship,
        customerAddressFiscalCode: wizardData.customerAddressFiscalCode,
        customerCity: wizardData.customerCity,
      }, null, 2));

      console.log('\n📄 DETALLES DE LA FACTURA:');
      console.log(JSON.stringify({
        invoiceNumber: wizardData.invoiceNumber,
        invoiceNumberMode: wizardData.invoiceNumberMode,
        invoiceDate: wizardData.invoiceDate,
        dueDate: wizardData.dueDate,
        invoiceType: wizardData.invoiceType,
        documentType: wizardData.documentType,
        operationType: wizardData.operationType,
        containerShipping: wizardData.containerShipping,
        operationDestination: wizardData.operationDestination,
        operationNature: wizardData.operationNature,
        documentFormatGeneration: wizardData.documentFormatGeneration,
        paymentMethod: wizardData.paymentMethod,
        currency: wizardData.currency,
      }, null, 2));

      console.log('\n📦 ITEMS/PRODUCTOS:');
      console.log(`Total de items: ${wizardData.items.length}`);
      wizardData.items.forEach((item, index) => {
        console.log(`\n  Item ${index + 1}:`);
        console.log(JSON.stringify(item, null, 2));
      });

      console.log('\n📝 INFORMACIÓN ADICIONAL:');
      console.log(JSON.stringify({
        notes: wizardData.notes,
        reference: wizardData.reference,
        retention: wizardData.retention,
      }, null, 2));

      console.log('\n💰 TOTALES:');
      console.log(JSON.stringify({
        subtotal: wizardData.subtotal,
        totalTax: wizardData.totalTax,
        totalDiscount: wizardData.totalDiscount,
        totalIsc: wizardData.totalIsc,
        totalOti: wizardData.totalOti,
        shippingCost: wizardData.shippingCost,
        insuranceCost: wizardData.insuranceCost,
        grandTotal: wizardData.grandTotal,
      }, null, 2));

      console.log('\n💳 DETALLES DE PAGO:');
      console.log(JSON.stringify({
        amountPaid: wizardData.amountPaid,
        change: wizardData.change,
        balance: wizardData.balance,
      }, null, 2));

      console.log('\n🔍 OBJETO COMPLETO (wizardData):');
      console.log(JSON.stringify(wizardData, null, 2));

      console.log('\n' + '='.repeat(80));

      // MAPEAR AL FORMATO DE LA API
      console.log('\n' + '='.repeat(80));
      console.log('🔄 MAPEANDO AL FORMATO DE API DE ALUDRA');
      console.log('='.repeat(80));

      const apiPayload = mapInvoiceToAPIFormat(wizardData);

      // Attach companyId so backend can fix logo before Aludra generates CAFE
      const companyId = localStorage.getItem('selectedCompanyId');
      if (companyId) {
        (apiPayload as any)._portalCompanyId = companyId;
      }

      console.log('\n✅ OBJETO MAPEADO PARA ENVIAR AL API:');
      console.log(JSON.stringify(apiPayload, null, 2));

      console.log('\n' + '='.repeat(80));
      console.log('📤 ENVIANDO AL BACKEND...');
      console.log('='.repeat(80));

      // Enviar al backend con idempotency key
      const response = await invoiceService.createInvoice(apiPayload, idempotencyKey);

      console.log('\n' + '='.repeat(80));
      console.log('📥 RESPUESTA DEL BACKEND');
      console.log('='.repeat(80));
      console.log(JSON.stringify(response, null, 2));

      // Manejar respuesta según el código
      if (response.status === 'success') {
        console.log('✅ Factura procesada exitosamente (código 0920)');

        // Track successful invoice submission
        trackingHelpers.trackInvoiceSubmission({
          invoiceNumber: wizardData.invoiceNumber || 'N/A',
          documentType: wizardData.documentType,
          customerName: wizardData.customerName,
          totalAmount: wizardData.grandTotal,
          itemsCount: wizardData.items.length,
          paymentMethod: wizardData.paymentMethod,
          receiverType: wizardData.receiverType,
          isCorrection: wizardData.documentType === '04' || wizardData.documentType === '05',
          companyId: (response as any).companyId || 0,
        });

        setSuccess({
          isOpen: true,
          title: '¡Factura emitida exitosamente!',
          message: response.message || 'La factura ha sido procesada exitosamente por la DGI.',
          status: 'success',
          responseCode: response.responseCode,
          additionalInfo: undefined,
          electronicId: (response as any).electronicId,
        });
      } else if (response.status === 'pending') {
        console.log('⏳ Factura pendiente de aprobación (código 0260)');
        setSuccess({
          isOpen: true,
          title: 'Factura enviada',
          message: response.message || 'La factura está siendo procesada por la DGI.',
          status: 'pending',
          responseCode: response.responseCode,
          additionalInfo: 'Recibirás una notificación cuando el proceso se complete.',
          electronicId: (response as any).electronicId,
        });
      } else {
        console.error('❌ Error al procesar la factura');

        // Track failed invoice submission
        trackingHelpers.trackInvoiceSubmissionFailed(
          response.message || 'Unknown error',
          wizardData.documentType,
          (response as any).companyId || 0
        );

        setError({
          isOpen: true,
          title: 'Error al procesar la factura',
          message: response.message || 'Ocurrió un error al procesar la factura. Por favor revisa los datos e intenta nuevamente.',
          details: response.responseCode ? [{ code: response.responseCode, message: response.message }] : undefined,
        });
      }
    } catch (error: any) {
      console.error('❌ Error al emitir la factura:', error);

      // Extract error details from API response
      let errorMessage = 'Error desconocido';
      let errorDetails: ErrorDetail[] | undefined = undefined;

      if (error?.response?.data) {
        const errorData = error.response.data;

        // Get the main error message
        errorMessage = errorData.message || errorMessage;

        // Get error details if available
        if (errorData.details && Array.isArray(errorData.details)) {
          errorDetails = errorData.details;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Track failed invoice submission (exception)
      trackingHelpers.trackInvoiceSubmissionFailed(
        errorMessage,
        wizardData.documentType,
        0
      );

      if (assistantEnabled) {
        reportError({
          title: 'Error al emitir la factura',
          message: errorMessage,
          details: errorDetails,
          screen: 'emitir factura',
        });
      } else {
        setError({
          isOpen: true,
          title: 'Error al emitir la factura',
          message: errorMessage,
          details: errorDetails,
        });
      }
    } finally {
      setIsSubmitting(false);
      releaseCrossTabLock();
    }
  };

  const handleSuccessClose = () => {
    setSuccess({ ...success, isOpen: false });
    resetWizard();
    navigate('/home/main');
  };

  // Show loading spinner while checking certificates or loading reference
  if (isCheckingCertificates || isLoadingReference) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
        <p className="ml-4 text-secondary-600 dark:text-secondary-400">
          {isLoadingReference
            ? 'Cargando factura referenciada...'
            : 'Verificando certificados digitales...'}
        </p>
      </div>
    );
  }

  // Construct dynamic steps based on document mode
  const STEPS = wizardData.documentMode !== 'invoice' && wizardData.gDFRef
    ? [
        BASE_STEPS[0], // Cliente
        REFERENCE_STEP, // Referencia (solo para notas)
        { ...BASE_STEPS[1], id: 3 }, // Detalles
        { ...BASE_STEPS[2], id: 4 }, // Items
        { ...BASE_STEPS[3], id: 5 }, // Notas
        { ...BASE_STEPS[4], id: 6 }, // Revisar
      ]
    : BASE_STEPS;

  // Avance del wizard. Con notas de credito/debito STEPS tiene 6 pasos, asi
  // que el porcentaje se calcula sobre el largo real, no sobre un 5 fijo.
  const progressPct = Math.round(((currentStep + 1) / STEPS.length) * 100);

  // Dynamic title based on document mode
  const pageTitle =
    wizardData.documentMode === 'credit-note'
      ? 'Nueva Nota de Crédito'
      : wizardData.documentMode === 'debit-note'
      ? 'Nueva Nota de Débito'
      : 'Nueva Factura';

  const pageDescription =
    wizardData.documentMode === 'credit-note'
      ? 'Crear una nota de crédito referenciando una factura electrónica'
      : wizardData.documentMode === 'debit-note'
      ? 'Crear una nota de débito referenciando una factura electrónica'
      : 'Crear una nueva factura electrónica paso a paso';

  return (
    <div className="font-display" data-testid="invoice-wizard-container">
      {/* Cabecera */}
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1), mas grande —
            no un breadcrumb chico seguido de un h1 que repite {pageTitle} dos
            veces. "Inicio" y "/" se quedan al tamaño de breadcrumb; el titulo
            dinamico (Factura / Nota de Crédito / Nota de Débito) salta al
            tamaño de titulo en la misma linea.
          */}
          <nav className="flex items-baseline gap-2">
            <Link to={homeRoute} className="text-[14px] text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white">
              Inicio
            </Link>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1
              className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white"
              data-testid="invoice-wizard-title"
            >
              {pageTitle}
            </h1>
          </nav>
          <p
            className="mt-2 text-sm text-secondary-500 dark:text-secondary-400"
            data-testid="invoice-wizard-description"
          >
            {pageDescription}
          </p>
        </div>

        {/* Indicador de avance */}
        <div className="inline-flex items-center gap-3">
          <div className="text-right">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-secondary-400">
              Progreso
            </div>
            <div className="mt-0.5 inline-flex items-baseline gap-0.5">
              <span className="bg-gradient-to-br from-teal-500 to-[#6b239e] bg-clip-text text-[26px] font-extrabold tracking-[-0.03em] text-transparent">
                {progressPct}
              </span>
              <span className="text-[14px] font-bold text-secondary-400">%</span>
            </div>
          </div>
          <div className="h-10 w-px bg-[#ececec] dark:bg-secondary-700" />
          <div className="font-mono text-xs text-secondary-500">
            Paso <span className="font-bold text-ink-850 dark:text-white">{currentStep + 1}</span> /{' '}
            {STEPS.length}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      {/*
        Compactado: antes cada paso llevaba número + nombre + descripción
        apilados (3 líneas) dentro de una Card con padding 24px, ~110px de
        alto total. Solo el nombre del paso ya identifica de qué se trata
        (la descripción larga era redundante con el título de cada Step);
        quitarla y ajustar círculo/padding baja la barra a ~60px sin perder
        navegación ni el estado (completado/actual/pendiente) de cada paso.
        Colores alineados al acento teal del resto del sistema (antes usaba
        `primary-600`, un azul que no aparece en ninguna otra pantalla
        rediseñada).
      */}
      <div className="mt-5 rounded-2xl border border-[#ececec] bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(10,34,51,.03),0_8px_20px_-10px_rgba(10,34,51,.10)] dark:border-secondary-700 dark:bg-secondary-800 sm:px-5">
        <nav aria-label="Progress" data-testid="invoice-wizard-steps-nav">
          <ol className="flex items-center" data-testid="invoice-wizard-steps-list">
            {STEPS.map((step, index) => (
              <li key={step.id} className="relative flex-1">
                {/* Connection Line - Show on all steps except the first half of step 1 */}
                {index !== STEPS.length - 1 && (
                  <div className="absolute left-0 right-0 top-4 flex items-center" style={{ zIndex: 0 }}>
                    <div
                      className={`h-0.5 w-full transition-all duration-300 ${
                        currentStep > index + 1
                          ? 'bg-teal-500'
                          : currentStep === index + 1
                          ? 'bg-gradient-to-r from-teal-500 to-secondary-300'
                          : 'bg-secondary-300 dark:bg-secondary-600'
                      }`}
                    />
                  </div>
                )}

                {/* Connection Line coming INTO this step (for last step) */}
                {index === STEPS.length - 1 && index > 0 && (
                  <div className="absolute left-0 right-0 top-4 flex items-center" style={{ zIndex: 0 }}>
                    <div
                      className={`h-0.5 w-full transition-all duration-300 ${
                        currentStep >= index
                          ? 'bg-teal-500'
                          : 'bg-secondary-300 dark:bg-secondary-600'
                      }`}
                    />
                  </div>
                )}

                <div className="flex items-center relative" style={{ zIndex: 1 }}>

                  <button
                    onClick={() => goToStep(index)}
                    className={`group relative flex w-full flex-col items-center rounded-xl py-1 transition-colors ${
                      currentStep >= index ? 'cursor-pointer' : 'cursor-not-allowed'
                    } ${currentStep === index ? 'bg-teal-500/[.07]' : ''}`}
                    disabled={currentStep < index}
                    data-testid={`invoice-wizard-step-nav-button-${step.id}`}
                  >
                    {/* El paso actual resalta con relleno solido + halo (glow):
                        antes solo tenia un borde teal de 2px, casi igual de
                        discreto que un paso futuro (borde gris). Completado
                        usa el mismo relleno pero con check en vez de numero,
                        para no confundirse con "actual". */}
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full transition-all ${
                        currentStep > index
                          ? 'bg-teal-500'
                          : currentStep === index
                          ? 'bg-teal-500 shadow-[0_0_0_5px_rgba(18,196,178,0.20)]'
                          : 'border-2 border-secondary-300 bg-white dark:bg-secondary-900'
                      }`}
                    >
                      {currentStep > index ? (
                        <CheckIcon className="h-4 w-4 text-white" strokeWidth={3} />
                      ) : (
                        <span
                          className={`text-[12px] font-bold ${
                            currentStep === index ? 'text-white' : 'text-secondary-500'
                          }`}
                        >
                          {step.id}
                        </span>
                      )}
                    </span>
                    <span
                      className={`mt-1.5 hidden text-[11px] md:block ${
                        currentStep === index
                          ? 'font-bold text-ink-850 dark:text-white'
                          : 'font-semibold text-secondary-500 dark:text-secondary-400'
                      }`}
                    >
                      {step.name}
                    </span>
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Step Content */}
      <div className="mt-5">
        {/* Step 0: Customer Selection (always) */}
        {currentStep === 0 && (
          <Step1CustomerSelection
            wizardData={wizardData}
            onUpdate={updateWizardData}
            onNext={nextStep}
          />
        )}

        {/* Step 1: Reference (only for credit/debit notes) OR Invoice Details */}
        {currentStep === 1 && wizardData.documentMode !== 'invoice' && wizardData.gDFRef && (
          <StepReference wizardData={wizardData} onNext={nextStep} onPrevious={previousStep} />
        )}
        {currentStep === 1 && wizardData.documentMode === 'invoice' && (
          <Step2InvoiceDetails
            wizardData={wizardData}
            onUpdate={updateWizardData}
            onNext={nextStep}
            onPrevious={previousStep}
          />
        )}

        {/* Step 2: Invoice Details (for credit/debit notes) OR Add Items */}
        {currentStep === 2 && wizardData.documentMode !== 'invoice' && wizardData.gDFRef && (
          <Step2InvoiceDetails
            wizardData={wizardData}
            onUpdate={updateWizardData}
            onNext={nextStep}
            onPrevious={previousStep}
          />
        )}
        {currentStep === 2 && wizardData.documentMode === 'invoice' && (
          <Step3AddItems
            wizardData={wizardData}
            onAddItem={addItem}
            onUpdateItem={updateItem}
            onRemoveItem={removeItem}
            onNext={nextStep}
            onPrevious={previousStep}
          />
        )}

        {/* Step 3: Add Items (for credit/debit notes) OR Additional Info */}
        {currentStep === 3 && wizardData.documentMode !== 'invoice' && wizardData.gDFRef && (
          <Step3AddItems
            wizardData={wizardData}
            onAddItem={addItem}
            onUpdateItem={updateItem}
            onRemoveItem={removeItem}
            onNext={nextStep}
            onPrevious={previousStep}
          />
        )}
        {currentStep === 3 && wizardData.documentMode === 'invoice' && (
          <Step4AdditionalInfo
            wizardData={wizardData}
            onUpdate={updateWizardData}
            onNext={nextStep}
            onPrevious={previousStep}
          />
        )}

        {/* Step 4: Additional Info (for credit/debit notes) OR Review */}
        {currentStep === 4 && wizardData.documentMode !== 'invoice' && wizardData.gDFRef && (
          <Step4AdditionalInfo
            wizardData={wizardData}
            onUpdate={updateWizardData}
            onNext={nextStep}
            onPrevious={previousStep}
          />
        )}
        {currentStep === 4 && wizardData.documentMode === 'invoice' && (
          <Step5Review
            wizardData={wizardData}
            onSubmit={handleSubmit}
            onPrevious={previousStep}
            isSubmitting={isSubmitting}
          />
        )}

        {/* Step 5: Review (for credit/debit notes only) */}
        {currentStep === 5 && wizardData.documentMode !== 'invoice' && wizardData.gDFRef && (
          <Step5Review
            wizardData={wizardData}
            onSubmit={handleSubmit}
            onPrevious={previousStep}
            isSubmitting={isSubmitting}
          />
        )}
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={success.isOpen}
        onClose={handleSuccessClose}
        title={success.title}
        message={success.message}
        status={success.status}
        responseCode={success.responseCode}
        additionalInfo={success.additionalInfo}
        electronicId={success.electronicId}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={error.isOpen}
        onClose={() => setError({ ...error, isOpen: false })}
        title={error.title}
        message={error.message}
        details={error.details}
      />

      {/* Modal de perfil incompleto (certificados requeridos) */}
      <ProfileIncompleteModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          navigate(homeRoute);
        }}
        onCompleteInfo={handleCompleteInfo}
      />
    </div>
  );
}
